import { apiRequest } from './client';

/**
 * FEAT-001 — the three credential flows that establish no session.
 *
 * These live here rather than on `AuthProvider` because none of them signs
 * anyone in: they ask for a link, or they set a password and then send the user
 * to the sign-in screen. `AuthProvider` holds *session* state, and putting
 * these on it would imply they change it.
 *
 * All three return 204, and `apiRequest` resolves 204 to `undefined` — so the
 * absence of a throw is the whole of the success signal.
 */

/**
 * Asks for a reset link.
 *
 * **Resolves whether or not the address exists.** The server returns 204 for an
 * unknown address, and for a resigned employee, so that nobody can use this
 * endpoint to discover who has an account (FEAT-001). The caller must therefore
 * never render anything that distinguishes the two — see `ForgotPasswordPage`.
 *
 * It also invalidates any reset link already outstanding for that address, which
 * is why the screen warns that only the newest email works.
 */
export function requestPasswordReset(email: string): Promise<void> {
  return apiRequest<void>('/auth/forgot-password', { method: 'POST', body: { email } });
}

/**
 * Sets a new password from a reset token.
 *
 * Throws `token_consumed` (410) if the link has been used or has expired — there
 * is deliberately no way to ask whether a token is valid without spending it.
 * On success the server revokes **every** session for that user, so a caller
 * that was signed in must clear its own state rather than assume it survived.
 */
export function resetPassword(token: string, password: string): Promise<void> {
  return apiRequest<void>('/auth/reset-password', { method: 'POST', body: { token, password } });
}

/** Sets the first password on an invited account. Same shape, different token. */
export function acceptInvitation(token: string, password: string): Promise<void> {
  return apiRequest<void>('/auth/accept-invitation', {
    method: 'POST',
    body: { token, password },
  });
}

/**
 * REQ-005 — changing your own password from inside a session.
 *
 * The current password is sent because the server demands it, and the server
 * demands it because a session is not proof of identity: an unlocked screen is
 * a session. It is not validated against the password rules on the way out —
 * an account created before a rule tightened must still be able to change away
 * from a password the platform now considers illegal.
 *
 * **Every session ends on success, including this one.** The caller has to sign
 * out locally afterwards, or it sits in a shell where every request 401s.
 */
export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return apiRequest<void>('/me/password', {
    method: 'POST',
    body: { current_password: currentPassword, new_password: newPassword },
  });
}
